import { motion } from "framer-motion";
import { FileText, BadgePercent, Star } from "lucide-react";

export default function ProductCard({ product, onBuy, index = 0 }) {
  const p = product;
  const off = p.original_price > p.price && p.price > 0 ? Math.round((1 - p.price / p.original_price) * 100) : 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.06, 0.3) }}
      className="card-lift group flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm"
      data-testid={`product-card-${p.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {p.cover_image_url ? (
          <img src={p.cover_image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-100 to-amber-50 text-slate-300">
            <FileText size={44} />
          </div>
        )}
        <span className="absolute left-4 top-4 rounded-full bg-ink/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-amber-400 backdrop-blur">
          {p.category}
        </span>
        {off > 0 && (
          <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold text-white">
            <BadgePercent size={12} /> {off}% OFF
          </span>
        )}
        {p.ptype === "course" && (
          <span className="absolute bottom-4 left-4 rounded-full bg-amber-500 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink font-bold">Course</span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-lg font-bold leading-snug text-slate-900 line-clamp-2">{p.title}</h3>
        <p className="mt-2 text-sm text-slate-500 line-clamp-2 flex-1">{p.description}</p>
        <div className="mt-3 flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-slate-400">
          {p.pages > 0 && <span>{p.pages} pages</span>}
          {p.size_label && <span>· {p.size_label}</span>}
        </div>
        {p.rating_count > 0 && (
          <div className="mt-2 flex items-center gap-2" data-testid={`product-rating-${p.id}`}>
            <span className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={13} className={n <= Math.round(p.rating_avg) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
              ))}
            </span>
            <span className="text-xs font-bold text-slate-600">{p.rating_avg}</span>
            <span className="text-xs text-slate-400">({p.rating_count})</span>
          </div>
        )}
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <span className="font-display text-2xl font-extrabold text-slate-900">₹{p.price === 0 ? "Free" : p.price.toLocaleString("en-IN")}</span>
            {p.original_price > p.price && p.price > 0 && (
              <span className="ml-2 text-sm text-slate-400 line-through">₹{p.original_price.toLocaleString("en-IN")}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onBuy(p, "preview")}
              data-testid={`pdf-preview-btn-${p.id}`}
              className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors"
            >
              Preview
            </button>
            <button
              onClick={() => onBuy(p, "buy")}
              data-testid={`buy-now-btn-${p.id}`}
              className="rounded-full bg-ink px-5 py-2 text-xs font-bold text-white hover:bg-amber-500 hover:text-ink transition-colors"
            >
              {p.price === 0 ? "Get Free" : "Buy Now"}
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
